import React, { Component, createRef } from "react";
import { TextEditor } from "./editor/TextEditor";
import { EditorToolbar } from "./editor/EditorToolbar";
import CoachPanel from "./Coach";
import ChapterAgentPanel from "./ChapterAgentPanel";
import StepResultsPanel from "./StepResultsPanel";
import IssueSolver from "./IssueSolver";
import SummarySidePanel from "./SummarySidePanel";
import ChapterHistoryPanel from "./ChapterHistoryPanel";
import WriterModeTimer from "./WriterModeTimer";
import PomodoroSessionsModal from "./PomodoroSessionsModal";
import { STEP_BUTTONS } from "./StepResultButtons";
import { Icon } from "../../components/Icon";
import { urlMap, getAuthHeaders, partsToPlainText } from "../../../utils";
import { mdToHtml, htmlToMd } from "../../../utils/markdown";
import { getPomodoroMinutes } from "../../../utils/userPrefs";
import { NotificationContext } from "../../../state/notifications/NotificationContext";
import { exportChapterPdf } from "../../../utils/pdfExport";


const TAB_STEP_PX = 32;
const SEARCH_HIT_CLASS = 'chapter-search-hit';
const SEARCH_HIT_ACTIVE_CLASS = 'chapter-search-hit--active';
const MIN_SEARCH_QUERY_LEN = 2;

const ESCAPE_RE = /[.*+?^${}()|[\]\\]/g;
const escapeRegExp = (value) => String(value || '').replace(ESCAPE_RE, '\\$&');

/** ---------- Parent Editor (class) ---------- */
class Container extends Component {
  static contextType = NotificationContext;
  constructor(props) {
    super(props);
    this.editorRef = createRef();
    this.autosaveTimer = null;
    this.readerAbortController = null;
    this.writerModeHintTimer = null;
    this.writerSaveAlertTimer = null;
    this.writerModeTimer = null;
    this.mobileViewportQuery = null;
    this.state = {
      autosave: true,
      wordCount: 0,
      lineHeight: "1.5",
      fontFamily: "inherit",
      writerModeHintVisible: false,
      writerModeSecondsLeft: getPomodoroMinutes() * 60,
      pomodoroPlannedSeconds: getPomodoroMinutes() * 60,
      writerModeTimerRunning: false,
      writerModeTimerPhase: 'work', // 'work' (25 min), 'reflection' (uncapped), or 'rest' (5 min)
      writerSessionId: null,
      writerCompletedCount: 0,
      pomodoroModalOpen: false,
      exportingPdf: false,
      // Coach state
      sessionId: null,
      chatMessages: [],
      selectedModel: null,
      coachMasterModelName: null,
      coachLoading: false,
      coachError: null,
      coachSending: false,
      coachTyping: false,
      seedsUsed: 0,
      searchHitCount: 0,
      activeSearchHitIndex: -1,
      isMobileViewport: false,
      writerModeMobileChatOpen: false,
      writerSaveError: null,
    };
  }

  componentDidMount() {
    const { chapter, showCoach } = this.props;
    // Make Enter split into <p> blocks (not zero-margin <div>s) so paragraph
    // gaps stay visible and match the \n\n paragraph-break markdown format.
    try { document.execCommand('defaultParagraphSeparator', false, 'p'); } catch { /* unsupported */ }
    if (this.editorRef.current) this.editorRef.current.innerHTML = mdToHtml(chapter?.content || "");
    this.applySearchHighlightFromProps();
    this.updateWordCount();
    if (showCoach && chapter?.id) this.ensureSessionAndLoad();
    if (chapter?.id) this.fetchWriterSessionCount(chapter.id);

    this.mobileViewportQuery = window.matchMedia('(max-width: 760px)');
    this.updateMobileViewportFlag();
    if (typeof this.mobileViewportQuery.addEventListener === 'function') {
      this.mobileViewportQuery.addEventListener('change', this.handleMobileViewportChange);
    } else if (typeof this.mobileViewportQuery.addListener === 'function') {
      this.mobileViewportQuery.addListener(this.handleMobileViewportChange);
    }
    
    // Listen for ESC key to exit Writer Mode
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('click', this.handleDocumentClick, true);
    this.emitHeaderControlsState();
  }
  componentDidUpdate(prevProps, prevState) {
    const { chapter, title, showCoach, searchQuery } = this.props;
    if (prevProps.chapter?.id !== chapter?.id) {
      if (this.editorRef.current) this.editorRef.current.innerHTML = mdToHtml(chapter?.content || "");
      this.applySearchHighlightFromProps();
      this.updateWordCount();
      this.stopWriterModeTimer(true);
      this.setState({ writerSessionId: null, writerCompletedCount: 0 });
      if (chapter?.id) this.fetchWriterSessionCount(chapter.id);
      // Reset coach state when chapter changes
      if (showCoach) this.ensureSessionAndLoad();
    }
    if (prevProps.title !== title && this.state.autosave) this.queueAutosave();
    // Load coach session when coach panel opens
    if (showCoach && !prevProps.showCoach && chapter?.id) {
      this.ensureSessionAndLoad();
    }
    if (!prevProps.isWriterMode && this.props.isWriterMode) {
      this.showWriterModeHint();
    }
    if (prevProps.searchQuery !== searchQuery) {
      this.applySearchHighlightFromProps();
    }
    if (
      prevState.autosave !== this.state.autosave ||
      prevState.writerCompletedCount !== this.state.writerCompletedCount ||
      prevState.exportingPdf !== this.state.exportingPdf
    ) {
      this.emitHeaderControlsState();
    }

    if (!prevProps.isWriterMode && this.props.isWriterMode && this.state.isMobileViewport) {
      this.setState({ writerModeMobileChatOpen: false });
    }

    if (prevProps.isWriterMode && !this.props.isWriterMode && this.state.writerModeMobileChatOpen) {
      this.setState({ writerModeMobileChatOpen: false });
    }

    if (prevProps.sidePanel !== this.props.sidePanel && this.props.sidePanel !== 'tasks' && this.state.writerModeMobileChatOpen) {
      this.setState({ writerModeMobileChatOpen: false });
    }
  }
  componentWillUnmount() { 
    if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
    if (this.writerModeHintTimer) clearTimeout(this.writerModeHintTimer);
    if (this.writerSaveAlertTimer) clearTimeout(this.writerSaveAlertTimer);
    this.stopWriterModeTimer(false);
    try {
      this.readerAbortController?.abort();
    } catch {}
    if (this.mobileViewportQuery) {
      if (typeof this.mobileViewportQuery.removeEventListener === 'function') {
        this.mobileViewportQuery.removeEventListener('change', this.handleMobileViewportChange);
      } else if (typeof this.mobileViewportQuery.removeListener === 'function') {
        this.mobileViewportQuery.removeListener(this.handleMobileViewportChange);
      }
      this.mobileViewportQuery = null;
    }
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('click', this.handleDocumentClick, true);
  }

  handleMobileViewportChange = () => {
    this.updateMobileViewportFlag();
  };

  updateMobileViewportFlag = () => {
    const isMobileViewport = !!this.mobileViewportQuery?.matches;
    this.setState((prev) => {
      if (prev.isMobileViewport === isMobileViewport) return null;
      return {
        isMobileViewport,
        writerModeMobileChatOpen: isMobileViewport ? prev.writerModeMobileChatOpen : false,
      };
    });
  };

  toggleWriterModeMobileChat = () => {
    this.setState((prev) => ({ writerModeMobileChatOpen: !prev.writerModeMobileChatOpen }));
  };

  closeWriterModeMobileChat = () => {
    this.setState({ writerModeMobileChatOpen: false });
  };

  // ----- Writer Mode -----
  handleKeyDown = (e) => {
    if (e.key === 'Escape' && this.props.isWriterMode) {
      e.preventDefault();
      this.exitWriterMode();
    }
  };

  handleDocumentClick = (e) => {
    if (!this.props.isWriterMode) return;
    const editor = this.editorRef.current;
    if (!editor) return;
    const target = e.target;
    if (editor.contains(target)) return;

    // Keep writer mode active while interacting with side panels (coach/issues/agent/results/summary).
    if (target instanceof Element) {
      const interactiveSidePanel = target.closest(
        '.coach-scroll, .coach-panel, .coach-input, .coach-textarea, .chapter-agent-scroll, .chapter-agent-panel, .step-results-scroll, .step-results-side-panel, .summary-side-scroll, .tasks-coach-panel, .writer-mode-timer, .writer-mode-chat-fab, .writer-mobile-chat-sheet, .writer-mobile-chat-shell'
      );
      if (interactiveSidePanel) return;
    }

    this.exitWriterMode();
  };

  showWriterModeHint = () => {
    if (this.writerModeHintTimer) clearTimeout(this.writerModeHintTimer);
    this.setState({ writerModeHintVisible: true });
    this.writerModeHintTimer = setTimeout(() => {
      this.setState({ writerModeHintVisible: false });
      this.writerModeHintTimer = null;
    }, 1800);
  };

  enterWriterMode = () => {
    this.props.onWriterModeChange?.(true);
  };

  exitWriterMode = () => {
    this.stopWriterModeTimer(true);
    this.props.onWriterModeChange?.(false);
  };

  startWriterModeTimer = () => {
    if (this.state.writerModeTimerRunning) return;

    const shouldResetTimer = this.state.writerModeSecondsLeft <= 0 && this.state.writerModeTimerPhase !== 'reflection';
    
    const startTimer = () => {
      if (this.writerModeTimer) clearInterval(this.writerModeTimer);
      this.writerModeTimer = setInterval(() => {
        this.setState((prev) => {
          if (prev.writerModeTimerPhase === 'reflection') {
            return { writerModeSecondsLeft: prev.writerModeSecondsLeft + 1 };
          }

          const secondsLeft = prev.writerModeSecondsLeft - 1;

          // Work phase complete: switch to uncapped reflection timer.
          if (prev.writerModeSecondsLeft === 1 && prev.writerModeTimerPhase === 'work') {
            if (this.state.isMobileViewport) {
              this.setState({ writerModeMobileChatOpen: true });
            }
            return { 
              writerModeSecondsLeft: 0,
              writerModeTimerPhase: 'reflection'
            };
          }
          
          // Check if rest phase is complete (was 1, now 0)
          if (prev.writerModeSecondsLeft === 1 && prev.writerModeTimerPhase === 'rest') {
            // Stop timer and finalize session
            if (this.writerModeTimer) {
              clearInterval(this.writerModeTimer);
              this.writerModeTimer = null;
            }
            this.finalizeWriterSession('completed');
            return { 
              writerModeSecondsLeft: 0, 
              writerModeTimerRunning: false,
              writerModeTimerPhase: 'work'
            };
          }
          
          // Normal countdown
          return { writerModeSecondsLeft: secondsLeft };
        });
      }, 1000);
    };
    
    if (shouldResetTimer) {
      const plannedSecs = getPomodoroMinutes() * 60;
      // Reset and start in one setState to avoid timing issues
      this.setState(
        { writerModeSecondsLeft: plannedSecs, pomodoroPlannedSeconds: plannedSecs, writerModeTimerPhase: 'work', writerModeTimerRunning: true },
        startTimer
      );
    } else {
      this.setState({ writerModeTimerRunning: true }, startTimer);
    }

    // Persist session start (fire-and-forget).
    this.createWriterSession();
  };

  stopWriterModeTimer = (reset = false) => {
    const wasRunning = this.state.writerModeTimerRunning;
    if (this.writerModeTimer) {
      clearInterval(this.writerModeTimer);
      this.writerModeTimer = null;
    }
    if (wasRunning && this.state.writerSessionId) {
      // User aborted mid-sprint or mid-rest; mark abandoned.
      this.finalizeWriterSession('abandoned');
    }
    this.setState({
      writerModeTimerRunning: false,
      writerModeSecondsLeft: reset ? this.state.pomodoroPlannedSeconds : this.state.writerModeSecondsLeft,
      writerModeTimerPhase: reset ? 'work' : this.state.writerModeTimerPhase,
    });
  };

  startRestTimerAfterReflection = () => {
    if (this.state.writerModeTimerPhase !== 'reflection') return;

    this.setState({
      writerModeTimerPhase: 'rest',
      writerModeSecondsLeft: 5 * 60,
    });
  };

  // ----- Writer session persistence -----
  openPomodoroModal = () => this.setState({ pomodoroModalOpen: true });
  setAutosave = (enabled) => this.setState({ autosave: !!enabled });

  emitHeaderControlsState = () => {
    this.props.onHeaderControlsChange?.({
      autosave: !!this.state.autosave,
      pomodoroCompletedCount: Number(this.state.writerCompletedCount) || 0,
      exportingPdf: !!this.state.exportingPdf,
    });
  };

  closePomodoroModal = () => {
    this.setState({ pomodoroModalOpen: false });
    const chapterId = this.props.chapter?.id;
    if (chapterId) this.fetchWriterSessionCount(chapterId);
  };

  fetchWriterSessionCount = async (chapterId) => {
    try {
      const res = await fetch(urlMap.writerSessionsCount(chapterId), {
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data?.chapterId !== chapterId) return;
      this.setState({ writerCompletedCount: Number(data.completedCount) || 0 });
    } catch {
      // non-fatal
    }
  };

  createWriterSession = async () => {
    if (this.state.writerSessionId) return this.state.writerSessionId;
    const chapterId = this.props.chapter?.id;
    if (!chapterId) return null;
    try {
      const res = await fetch(urlMap.createWriterSession, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ chapterId, plannedWritingMinutes: getPomodoroMinutes(), clientSource: 'web' }),
      });
      if (!res.ok) return null;
      const session = await res.json();
      if (session?.id) {
        this.setState({ writerSessionId: session.id });
        return session.id;
      }
      return null;
    } catch {
      // non-fatal
      return null;
    }
  };

  ensureWriterSessionForReflection = async () => {
    if (this.state.writerSessionId) return this.state.writerSessionId;
    return this.createWriterSession();
  };

  finalizeWriterSession = async (status) => {
    const sessionId = this.state.writerSessionId;
    if (!sessionId) return;
    const planned = this.state.pomodoroPlannedSeconds || getPomodoroMinutes() * 60;
    const elapsed = status === 'completed'
      ? planned
      : Math.max(0, planned - (this.state.writerModeSecondsLeft || 0));

    // Clear local handle immediately so re-entry can't double-finalize.
    this.setState({ writerSessionId: null });

    try {
      const res = await fetch(urlMap.updateWriterSession(sessionId), {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, actualWritingSeconds: elapsed }),
      });
      if (!res.ok) return;
      if (status === 'completed') {
        const chapterId = this.props.chapter?.id;
        if (chapterId) this.fetchWriterSessionCount(chapterId);
      }
    } catch {
      // non-fatal
    }
  };

  continueAbandonedSession = (session) => {
    if (!session) return;
    
    // Calculate remaining time from the abandoned session
    const plannedSeconds = (session.planned_writing_minutes || 25) * 60;
    const elapsedSeconds = session.actual_writing_seconds || 0;
    const remainingSeconds = Math.max(0, plannedSeconds - elapsedSeconds);
    
    // Close modal and set timer to resume from remaining time
    const chapterId = this.props.chapter?.id;
    this.setState(
      { 
        pomodoroModalOpen: false,
        writerModeSecondsLeft: remainingSeconds 
      },
      () => {
        if (chapterId) this.fetchWriterSessionCount(chapterId);
        this.enterWriterMode();
        this.startWriterModeTimer();
      }
    );
  };

  onExportPdf = async () => {
    if (this.state.exportingPdf) return;
    const chapterId = this.props.chapter?.id;
    if (!chapterId) {
      this.context?.add?.('Missing chapter id for export');
      return;
    }

    this.setState({ exportingPdf: true });
    try {
      await exportChapterPdf(chapterId);
      this.context?.add?.('PDF exported');
    } catch (err) {
      this.context?.add?.(err?.message || 'Failed to export PDF');
    } finally {
      this.setState({ exportingPdf: false });
    }
  };

  // ----- Public methods (exposed via ref) -----
  getPlainText = () => (this.editorRef.current?.innerText || "").trim();
  getHTML = () => this.editorRef.current?.innerHTML || "";
  focusEditor = () => this.editorRef.current?.focus();

  clearSearchHighlights = (root = this.editorRef.current) => {
    if (!root) return;
    const hits = root.querySelectorAll(`.${SEARCH_HIT_CLASS}`);
    hits.forEach((el) => {
      const parent = el.parentNode;
      if (!parent) return;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
    });
    this.setState({ searchHitCount: 0, activeSearchHitIndex: -1 });
  };

  getSearchHits = () => {
    const root = this.editorRef.current;
    if (!root) return [];
    return Array.from(root.querySelectorAll(`.${SEARCH_HIT_CLASS}`));
  };

  setActiveSearchHit = (hits, nextIndex, behavior = 'smooth') => {
    if (!Array.isArray(hits) || hits.length === 0) return false;

    const normalized = ((nextIndex % hits.length) + hits.length) % hits.length;
    hits.forEach((el) => el.classList.remove(SEARCH_HIT_ACTIVE_CLASS));

    const active = hits[normalized];
    active.classList.add(SEARCH_HIT_ACTIVE_CLASS);
    active.scrollIntoView({ behavior, block: 'center' });

    this.setState({ searchHitCount: hits.length, activeSearchHitIndex: normalized });
    return true;
  };

  focusNextSearchHit = () => {
    const hits = this.getSearchHits();
    if (hits.length === 0) return false;
    const nextIndex = this.state.activeSearchHitIndex >= 0
      ? this.state.activeSearchHitIndex + 1
      : 0;
    return this.setActiveSearchHit(hits, nextIndex);
  };

  focusPreviousSearchHit = () => {
    const hits = this.getSearchHits();
    if (hits.length === 0) return false;
    const prevIndex = this.state.activeSearchHitIndex >= 0
      ? this.state.activeSearchHitIndex - 1
      : hits.length - 1;
    return this.setActiveSearchHit(hits, prevIndex);
  };

  applySearchHighlightFromProps = () => {
    const root = this.editorRef.current;
    if (!root) return;

    const rawQuery = String(this.props.searchQuery || '').trim();
    this.clearSearchHighlights(root);
    if (rawQuery.length < MIN_SEARCH_QUERY_LEN) return;

    const regex = new RegExp(`(${escapeRegExp(rawQuery)})`, 'gi');
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
    const textNodes = [];

    let node;
    while ((node = walker.nextNode())) {
      if (!node.textContent || !node.textContent.trim()) continue;
      textNodes.push(node);
    }

    textNodes.forEach((textNode) => {
      const original = textNode.textContent || '';
      regex.lastIndex = 0;
      if (!regex.test(original)) return;

      regex.lastIndex = 0;
      const frag = document.createDocumentFragment();
      let cursor = 0;

      original.replace(regex, (match, _g1, offset) => {
        if (offset > cursor) {
          frag.appendChild(document.createTextNode(original.slice(cursor, offset)));
        }
        const mark = document.createElement('mark');
        mark.className = SEARCH_HIT_CLASS;
        mark.textContent = match;
        frag.appendChild(mark);
        cursor = offset + match.length;
        return match;
      });

      if (cursor < original.length) {
        frag.appendChild(document.createTextNode(original.slice(cursor)));
      }

      textNode.parentNode?.replaceChild(frag, textNode);
    });

    const hits = this.getSearchHits();
    if (hits.length > 0) {
      this.setActiveSearchHit(hits, 0);
    }
  };

  getEditorHtmlForSave = () => {
    const root = this.editorRef.current;
    if (!root) return '';
    const clone = root.cloneNode(true);
    if (clone instanceof HTMLElement) {
      const hits = clone.querySelectorAll(`.${SEARCH_HIT_CLASS}`);
      hits.forEach((el) => {
        const parent = el.parentNode;
        if (!parent) return;
        while (el.firstChild) parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
      });
      return clone.innerHTML;
    }
    return root.innerHTML || '';
  };

  // Scroll the editor to the first occurrence of targetText and select it.
  scrollToText = (targetText) => {
    const root = this.editorRef.current;
    if (!root || !targetText) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
    let node;
    while ((node = walker.nextNode())) {
      const idx = node.textContent ? node.textContent.indexOf(targetText) : -1;
      if (idx !== -1) {
        const range = document.createRange();
        range.setStart(node, idx);
        range.setEnd(node, idx + targetText.length);
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(range);
        }
        node.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        break;
      }
    }
  };

  // ----- Saving -----
  showWriterSaveError = (message) => {
    if (this.writerSaveAlertTimer) clearTimeout(this.writerSaveAlertTimer);
    this.setState({ writerSaveError: message || 'Save failed. Your edits are still local only.' });
    this.writerSaveAlertTimer = setTimeout(() => {
      this.writerSaveAlertTimer = null;
      this.setState({ writerSaveError: null });
    }, 9000);
  };

  saveNow = async () => {
    const { chapter, title, onSave } = this.props;
    if (!chapter || !onSave) return;
    const content = htmlToMd(this.getEditorHtmlForSave());
    try {
      await onSave({ chapterId: chapter.id, title: title || "", content });
      if (this.state.writerSaveError) {
        this.setState({ writerSaveError: null });
      }
    } catch (err) {
      const detail = String(err?.message || '').trim();
      const msg = detail
        ? `Save failed in writer mode. Your edits are still local and not synced. (${detail})`
        : 'Save failed in writer mode. Your edits are still local and not synced.';
      this.showWriterSaveError(msg);
    }
  };
  queueAutosave = () => {
    if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
    this.autosaveTimer = setTimeout(this.saveNow, 1200);
  };

  // ----- Selection / formatting helpers -----
  getSelection = () => {
    const sel = window.getSelection?.();
    return sel && sel.rangeCount ? sel : null;
  };
  isBlockEl = (node) => {
    if (!node || node.nodeType !== 1) return false;
    const tag = node.tagName.toLowerCase();
    if (tag === "p" || tag === "div" || tag === "li") return true;
    const disp = getComputedStyle(node).display;
    return disp === "block" || disp === "list-item";
  };
  closestBlock = (node) => {
    let n = node;
    if (!n) return null;
    if (n.nodeType === Node.TEXT_NODE) n = n.parentNode;
    while (n && n !== this.editorRef.current) {
      if (this.isBlockEl(n)) return n;
      n = n.parentNode;
    }
    return this.editorRef.current;
  };
  getBlocksInSelection = (range) => {
    const root = this.editorRef.current;
    const walker = document.createTreeWalker(
      root,
      NodeFilter.SHOW_ELEMENT,
      {
        acceptNode: (node) =>
          range.intersectsNode(node) && this.isBlockEl(node)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT,
      },
      false
    );
    const blocks = new Set();
    let n = walker.currentNode;
    while ((n = walker.nextNode())) blocks.add(n);
    if (blocks.size === 0) {
      const sel = this.getSelection();
      if (sel) {
        const blk = this.closestBlock(sel.anchorNode);
        if (blk && blk !== root) blocks.add(blk);
      }
    }
    return Array.from(blocks);
  };
  unwrapElement = (el) => {
    const parent = el.parentNode;
    if (!parent) return;
    while (el.firstChild) parent.insertBefore(el.firstChild, el);
    parent.removeChild(el);
  };

  // ----- Google-Docs-like Tab behavior -----
  isInsideListItem = (node) => {
    let n = node;
    while (n && n !== this.editorRef.current) {
      if ((n.tagName || "").toLowerCase() === "li") return true;
      n = n.parentNode;
    }
    return false;
  };
  isCaretAtStartOfBlock = (block) => {
    const sel = this.getSelection();
    if (!sel || !sel.isCollapsed) return false;
    const range = sel.getRangeAt(0).cloneRange();
    const pre = document.createRange();
    pre.selectNodeContents(block);
    pre.setEnd(range.startContainer, range.startOffset);
    const txt = pre.toString().replace(/\u200B/g, "");
    return txt.trim().length === 0;
  };
  adjustFirstLineIndent = (block, direction) => {
    const current = parseFloat(getComputedStyle(block).textIndent) || 0;
    let next = current + direction * TAB_STEP_PX;
    if (next < 0) next = 0;
    block.style.textIndent = `${next}px`;
  };
  insertVisualTab = () => {
    try { document.execCommand("insertHTML", false, "&emsp;"); }
    catch { document.execCommand("insertHTML", false, "&nbsp;&nbsp;&nbsp;&nbsp;"); }
  };
  moveCaretByWord = (forward = true) => {
    const sel = this.getSelection();
    if (!sel || typeof sel.modify !== "function") return false;
    sel.modify("move", forward ? "forward" : "backward", "word");
    return true;
  };

  // ----- Editing & autosave -----
  updateWordCount = () => {
    const text = this.editorRef.current?.innerText || "";
    const count = text.trim() === "" ? 0 : text.trim().split(/\s+/).length;
    this.setState({ wordCount: count });
  };
  onEditorInput = () => {
    if (this.editorRef.current?.querySelector(`.${SEARCH_HIT_CLASS}`)) {
      this.clearSearchHighlights(this.editorRef.current);
    }
    this.updateWordCount();
    if (this.state.autosave) this.queueAutosave();
    // Auto-enter Writer Mode on first input
    if (!this.props.isWriterMode) {
      this.enterWriterMode();
    }
  };
  onPaste = (e) => {
    const html = e.clipboardData?.getData("text/html");
    if (!html) return;
    e.preventDefault();

    const div = document.createElement("div");
    div.innerHTML = html;

    const walk = (node) => {
      if (node.nodeType !== 1) return;
      const el = /** @type {HTMLElement} */ (node);
      Array.from(el.attributes).forEach((a) => {
        if (/^mso-|^class$/i.test(a.name) && /Mso/.test(a.value)) el.removeAttribute(a.name);
      });
      if (el.getAttribute("style")) {
        el.style.backgroundColor = "";
        el.style.color = "";
        el.style.fontSize = "";
        el.style.fontFamily = "";
      }
      if (el.tagName.toLowerCase() === "font") {
        const span = document.createElement("span");
        span.innerHTML = el.innerHTML;
        el.parentNode?.replaceChild(span, el);
      }
      Array.from(el.childNodes).forEach(walk);
    };
    walk(div);

    document.execCommand("insertHTML", false, div.innerHTML);
    this.onEditorInput();
  };
  onEditorKeyDown = (e) => {
    if (e.key !== "Tab") return;
    const sel = this.getSelection();
    if (!sel) return;

    if (this.isInsideListItem(sel.anchorNode)) {
      e.preventDefault();
      this.exec(e.shiftKey ? "outdent" : "indent");
      return;
    }
    e.preventDefault();
    this.moveCaretByWord(!e.shiftKey);
  };
  exec = (cmd, value = null) => {
    if (!this.editorRef.current) return;
    this.editorRef.current.focus();
    try { document.execCommand("styleWithCSS", false, true); } catch {}
    document.execCommand(cmd, false, value);
    this.onEditorInput();
  };

  // --- Font + line height ---
  onFontChange = (e) => {
    const family = e.target.value;
    this.setState({ fontFamily: family });
    this.exec("fontName", family);
  };
  applyLineHeightToSelection = (lh) => {
    const sel = this.getSelection();
    if (!sel) return;
    const range = sel.getRangeAt(0);
    const blocks = this.getBlocksInSelection(range);
    blocks.forEach((b) => { b.style.lineHeight = lh; });
  };
  onLineHeightChange = (e) => {
    const lh = e.target.value;
    this.setState({ lineHeight: lh });
    this.applyLineHeightToSelection(lh);
    if (this.state.autosave) this.queueAutosave();
  };

  // --- Clear formatting ---
  clearFormatting = () => {
    const sel = this.getSelection();
    if (!sel) return;
    this.exec("removeFormat");
    try { document.execCommand("unlink"); } catch {}
    const range = sel.getRangeAt(0);
    const blocks = this.getBlocksInSelection(range);
    blocks.forEach((b) => {
      b.style.textIndent = "";
      b.style.textAlign = "";
      b.style.lineHeight = "";
      b.style.fontFamily = "";
      b.style.backgroundColor = "";
      b.style.color = "";
    });
    blocks.forEach((b) => {
      b.querySelectorAll("font").forEach((fontEl) => this.unwrapElement(fontEl));
    });
    this.onEditorInput();
  };

  // --- Coach Backend Methods ---
  async ensureSessionAndLoad() {
    const { chapter } = this.props;
    if (!chapter?.id) return;

    this.setState({ coachLoading: true, coachError: null });

    let selectedModel = this.state.selectedModel;
    if (!selectedModel) {
      selectedModel = await this.fetchCoachMasterModel();
      if (!selectedModel) {
        this.setState({ coachLoading: false, coachError: "No master model configured" });
        return;
      }
    }

    const sessions = await this.fetchSessions(selectedModel, chapter.id);
    let sessionId = sessions?.[0]?.id || null;

    if (!sessionId) {
      const created = await this.createSession(selectedModel, chapter.id, "Coach");
      sessionId = created?.id || null;
    }

    this.setState({ sessionId }, () => {
      if (sessionId) this.fetchMessages(sessionId);
      else this.setState({ coachLoading: false, coachError: "Could not create session" });
    });
  }

  async fetchCoachMasterModel() {
    try {
      const res = await fetch(urlMap.listLlms() + "/master", {
        method: "GET",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const model = data?.model;
      if (!model?.id) throw new Error("No master model configured");
      this.setState({ selectedModel: model.id, coachMasterModelName: model.name || model.id });
      return model.id;
    } catch (err) {
      this.setState({ coachError: err.message || "Failed to load master model" });
      return null;
    }
  }

  async fetchSessions(selectedModelKey, chapterId) {
    try {
      const url = new URL(urlMap.listChatSessions(chapterId), window.location.origin);
      if (selectedModelKey) url.searchParams.append("model", selectedModelKey);

      const res = await fetch(url.toString(), {
        method: "GET",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      this.setState({ coachError: err.message || "Failed to load sessions" });
      return [];
    }
  }

  async createSession(modelKey, chapterId, title = "Coach") {
    try {
      const res = await fetch(urlMap.createChatSession, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ llmId: modelKey, chapterId, title }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      this.setState({ coachError: err.message || "Failed to create session" });
      return null;
    }
  }

  switchCoachSession = async (sessionId) => {
    this.setState({ sessionId, chatMessages: [], coachLoading: true, coachError: null });
    await this.fetchMessages(sessionId);
  };

  async fetchMessages(sessionId) {
    try {
      const res = await fetch(urlMap.listChatMessages() + "?sessionId=" + sessionId, {
        method: "GET",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const visibleData = data.filter((m) => m.role !== "system");
      const bubbles = visibleData.map((m) => ({
        id: m.id,
        from: m.role === "user" ? "me" : "them",
        text: partsToPlainText(m.content),
      }));
      this.setState({ chatMessages: bubbles, coachLoading: false, coachError: null });
    } catch (err) {
      this.setState({ coachError: err.message || "Failed to load messages", coachLoading: false });
    }
  }

  async sendUserMessage(sessionId, text) {
    const { selectedModel } = this.state;
    if (!selectedModel) {
      this.setState({ coachError: "No master model configured" });
      return null;
    }

    try {
      const res = await fetch(urlMap.createChatMessage(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          sessionId,
          llmId: selectedModel,
          content: [{ type: 'text', text }],
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      this.setState({ coachError: err.message || 'Failed to send message' });
      return null;
    }
  }

  onCoachSend = async (text) => {
    const { sessionId } = this.state;
    if (!sessionId || !text.trim()) return;

    this.setState((s) => ({
      coachSending: true,
      coachTyping: true,
      chatMessages: [...s.chatMessages, { id: `local-${Date.now()}`, from: "me", text }],
    }));

    const run = await this.sendUserMessage(sessionId, text);
    if (!run?.runId) {
      this.setState({ coachSending: false, coachTyping: false });
      return;
    }

    await this.streamAssistantSSE(sessionId, run.runId);
  };

  async streamAssistantSSE(sessionId, runId) {
    const streamUrl = new URL(urlMap.streamRun(runId), window.location.origin);
    streamUrl.searchParams.set("sessionId", sessionId);

    const draftId = `assist-${runId}`;
    this.setState((s) => ({
      chatMessages: [...s.chatMessages, { id: draftId, from: "them", text: "" }],
    }));

    this.readerAbortController = new AbortController();

    try {
      const res = await fetch(streamUrl.toString(), {
        method: "GET",
        headers: { ...getAuthHeaders(), Accept: "text/event-stream" },
        signal: this.readerAbortController.signal,
      });

      if (!res.ok || !res.body) {
        this.updateAssistantBubble(draftId, "⚠️ Stream failed.");
        this.setState({ coachSending: false });
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assembled = "";

      const feed = (chunk) => {
        buffer += chunk;
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";

        for (const frame of frames) {
          const lines = frame.split("\n");
          for (const line of lines) {
            const t = line.trim();
            if (!t.startsWith("data:")) continue;
            const json = t.slice(5).trim();

            try {
              const evt = JSON.parse(json);
              const kind = evt.event ?? evt.type;
              const delta = evt.delta ?? evt.payload?.delta;

              if (kind === "delta" && delta?.type === "text" && typeof delta.text === "string") {
                assembled += delta.text;
                this.updateAssistantBubble(draftId, assembled);
              } else if (kind === "done") return true;
            } catch {}
          }
        }
        return false;
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        let chunk = decoder.decode(value, { stream: true });
        let isTyping = true;

        if (chunk && isTyping) {
          this.setState({ coachTyping: false });
          isTyping = false;
        }
        const finished = feed(chunk);
        if (finished) break;
      }
      if (buffer) feed(decoder.decode());

      this.updateAssistantBubble(draftId, assembled || "(no response)");
      this.setState({ coachTyping: false });

      // Charge 1 seed only when there is an actual response
      if (assembled.trim()) {
        this.setState(s => ({ seedsUsed: s.seedsUsed + 1 }));
        this.context?.notifyWalletRefresh?.();
      }
    } catch (err) {
      if (err?.name !== "AbortError") this.updateAssistantBubble(draftId, "⚠️ Connection lost.");
      this.setState({ coachTyping: false });
    } finally {
      this.setState({ coachSending: false });
      this.readerAbortController = null;
    }
  }

  updateAssistantBubble(id, text) {
    this.setState(
      (s) => ({
        chatMessages: s.chatMessages.map((m) => (m.id === id ? { ...m, text } : m)),
      }),
      () => {
        const el = document.querySelector(".coach-messages");
        if (el) el.scrollTop = el.scrollHeight;
      }
    );
  }

  onCoachReset = async () => {
    const { chapter } = this.props;
    let { selectedModel } = this.state;
    if (!chapter?.id) return;

    if (!selectedModel) {
      selectedModel = await this.fetchCoachMasterModel();
      if (!selectedModel) {
        this.setState({ coachError: "No master model configured" });
        return;
      }
    }

    const created = await this.createSession(selectedModel, chapter.id, "Coach");
    const sessionId = created?.id || null;

    if (sessionId) {
      this.setState({ sessionId, chatMessages: [], coachLoading: true }, async () => {
        await this.fetchMessages(sessionId);
      });
    } else {
      this.setState({ coachError: "Failed to reset chat" });
    }
  };

  render() {
    const { saving, showCoach, sidePanel, onToggleCoach, onToggleAgent, onClosePanel, chapter, projectId, reflectSignal, onReflectionConsumed } = this.props;
    const {
      autosave, wordCount, lineHeight, fontFamily,
      chatMessages, coachLoading, coachError, coachSending, coachTyping, coachMasterModelName,
      seedsUsed, writerModeSecondsLeft, writerModeTimerRunning, exportingPdf,
      searchHitCount, activeSearchHitIndex,
      isMobileViewport, writerModeMobileChatOpen, writerSaveError,
    } = this.state;
    const isWriterMode = !!this.props.isWriterMode;
    const isRestPhase = this.state.writerModeTimerPhase === 'rest';

    const showAgent = sidePanel === 'agent';
    const showTasks = sidePanel === 'tasks';
    const showSummary = sidePanel === 'summary';
    const showHistory = sidePanel === 'history';
    const showReflections = sidePanel === 'reflections';
    const showStepResults = sidePanel?.startsWith('step:');
    const activeStepName = showStepResults ? sidePanel.slice(5) : null;
    const activeStepLabel = activeStepName
      ? (STEP_BUTTONS.find(b => b.key === activeStepName)?.label || activeStepName)
      : '';
    const hasSidePanel = sidePanel != null;
    const showPanels = !isWriterMode;
    const useWriterMobileLayout = isWriterMode && isMobileViewport && showTasks;
    const { writerModeHintVisible } = this.state;

    return (
      <div className={`editor-root focus-lock ${hasSidePanel ? "show-coach" : ""} ${isWriterMode ? "writer-mode" : ""}${useWriterMobileLayout ? ' writer-mobile-layout' : ''}${useWriterMobileLayout && writerModeMobileChatOpen ? ' writer-mobile-chat-open' : ''}`}>
        {isWriterMode && (
          <>
            <div className={`writer-mode-hint${writerModeHintVisible ? ' is-visible' : ''}`} role="status" aria-live="polite">
              Esc to exit writer mode
            </div>
            {writerSaveError && (
              <div className="writer-mode-save-alert" role="alert" aria-live="assertive">
                {writerSaveError}
              </div>
            )}
          </>
        )}

        {!isWriterMode && (
          <EditorToolbar
            exec={this.exec}
            fontFamily={fontFamily}
            onFontChange={this.onFontChange}
            lineHeight={lineHeight}
            onLineHeightChange={this.onLineHeightChange}
            wordCount={wordCount}
            onClearFormatting={this.clearFormatting}
            saving={saving}
            lastSavedAt={this.props.lastSavedAt}
            lastCommittedAt={this.props.lastCommittedAt}
            committingToHistory={this.props.committingToHistory}
            timerControl={(
              <WriterModeTimer
                className="writer-mode-timer--toolbar"
                secondsLeft={writerModeSecondsLeft}
                timerRunning={writerModeTimerRunning}
                timerPhase={this.state.writerModeTimerPhase}
                onStart={this.startWriterModeTimer}
              />
            )}
            onToggleCoach={onToggleCoach}
            onToggleAgent={onToggleAgent}
            showCoach={showCoach}
            sidePanel={sidePanel}
            onSave={this.saveNow}
          />
        )}

        <div className="editor-shell">
          {searchHitCount > 1 && (
            <div className="chapter-search-scroll-hint" role="status" aria-live="polite">
              <span className="chapter-search-scroll-hint-label">
                Next match below: {activeSearchHitIndex + 1}/{searchHitCount}
              </span>
              <button
                type="button"
                className="chapter-search-scroll-hint-btn"
                onClick={this.focusPreviousSearchHit}
                aria-label="Previous search match"
                title="Previous search match"
              >
                ↑
              </button>
              <button
                type="button"
                className="chapter-search-scroll-hint-btn"
                onClick={this.focusNextSearchHit}
                aria-label="Next search match"
                title="Next search match"
              >
                ↓
              </button>
            </div>
          )}

          <TextEditor
            editorRef={this.editorRef}
            isWriterMode={isWriterMode}
            readOnly={isWriterMode && isRestPhase}
            onInput={this.onEditorInput}
            onKeyDown={this.onEditorKeyDown}
            onPaste={this.onPaste}
            topContent={isWriterMode ? (
              <div className="writer-mode-inline-timer">
                <WriterModeTimer
                  secondsLeft={writerModeSecondsLeft}
                  timerRunning={writerModeTimerRunning}
                  timerPhase={this.state.writerModeTimerPhase}
                  onStart={this.startWriterModeTimer}
                />
              </div>
            ) : null}
          />

          {showCoach && showPanels && (
            <CoachPanel              
              chapterId={chapter.id}
              messages={chatMessages}
              loading={coachLoading}
              error={coachError}
              sending={coachSending}
              typing={coachTyping}
              masterModelName={coachMasterModelName}
              onClose={onToggleCoach}
              onSend={this.onCoachSend}
              onReset={this.onCoachReset}
              onSwitchSession={this.switchCoachSession}
              currentSessionId={this.state.sessionId}
              seedsUsed={seedsUsed}
            />
          )}

          {showAgent && showPanels && (
            <ChapterAgentPanel
              projectId={projectId}
              chapterId={chapter.id}
              chapter={chapter}
              onClose={onToggleAgent}
            />
          )}

          {showTasks && (
            <div className="tasks-side-stack">
              {useWriterMobileLayout ? (
                <div className={`writer-mobile-chat-sheet${writerModeMobileChatOpen ? ' is-open' : ''}`} aria-hidden={!writerModeMobileChatOpen}>
                  <button
                    type="button"
                    className="writer-mobile-chat-backdrop"
                    aria-label="Close chat"
                    onClick={this.closeWriterModeMobileChat}
                  />
                  <div className="writer-mobile-chat-shell">
                    <IssueSolver
                      metadata={chapter.metadata || {}}
                      chapter={chapter}
                      projectId={projectId}
                      projectIssues={this.props.projectIssues || []}
                      onClose={this.closeWriterModeMobileChat}
                      onOpenInEditor={this.scrollToText}
                      writerModeTimerPhase={this.state.writerModeTimerPhase}
                      writerModeSecondsLeft={this.state.writerModeSecondsLeft}
                      chapterText={this.state.text}
                      writerSessionId={this.state.writerSessionId}
                      ensureWriterSessionForReflection={this.ensureWriterSessionForReflection}
                      reflectSignal={reflectSignal}
                      onReflectionConsumed={onReflectionConsumed}
                      onReflectionComplete={this.startRestTimerAfterReflection}
                    />
                  </div>
                </div>
              ) : (
                <IssueSolver
                  metadata={chapter.metadata || {}}
                  chapter={chapter}
                  projectId={projectId}
                  projectIssues={this.props.projectIssues || []}
                  onClose={onClosePanel}
                  onOpenInEditor={this.scrollToText}
                  writerModeTimerPhase={this.state.writerModeTimerPhase}
                  writerModeSecondsLeft={this.state.writerModeSecondsLeft}
                  chapterText={this.state.text}
                  writerSessionId={this.state.writerSessionId}
                  ensureWriterSessionForReflection={this.ensureWriterSessionForReflection}
                  reflectSignal={reflectSignal}
                  onReflectionConsumed={onReflectionConsumed}
                  onReflectionComplete={this.startRestTimerAfterReflection}
                />
              )}
            </div>
          )}

          {showStepResults && showPanels && (
            <StepResultsPanel
              stepName={activeStepName}
              stepLabel={activeStepLabel}
              metadata={chapter.metadata || {}}
              chapter={chapter}
              projectId={projectId}
              projectIssues={this.props.projectIssues || []}
              onClose={onClosePanel}
              onOpenInEditor={this.scrollToText}
            />
          )}

          {showSummary && showPanels && (
            <SummarySidePanel
              chapter={chapter}
              projectId={projectId}
              onClose={onClosePanel}
            />
          )}

          {showHistory && showPanels && (
            <ChapterHistoryPanel
              chapterId={chapter?.id}
              projectId={projectId}
              currentContent={chapter?.content ?? ''}
              onClose={onClosePanel}
              onCommitToHistory={this.props.onCommitToHistory}
              committingToHistory={this.props.committingToHistory}
            />
          )}

          {showReflections && showPanels && chapter?.id && (
            <PomodoroSessionsModal
              embedded
              chapterId={chapter.id}
              chapterTitle={chapter.title}
              onClose={onClosePanel}
              onContinueSession={this.continueAbandonedSession}
            />
          )}
        </div>

        {useWriterMobileLayout && (
          <button
            type="button"
            className={`writer-mode-chat-fab${writerModeMobileChatOpen ? ' is-open' : ''}`}
            onClick={this.toggleWriterModeMobileChat}
            aria-label={writerModeMobileChatOpen ? 'Close coach chat' : 'Open coach chat'}
            title={writerModeMobileChatOpen ? 'Close coach chat' : 'Open coach chat'}
          >
            <Icon.Chat />
          </button>
        )}

      </div>
    );
  }
}

export default React.forwardRef((props, ref) => <Container {...props} ref={ref} />);